import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { UserCircle2, Star, IdCard, Building2 } from "lucide-react";
import { toast } from "sonner";
import RowActions from "../../components/RowActions";
import { useSchools, SchoolSelect } from "../../components/SchoolSelect";
import { localName, localFullName } from "../../lib/localName";

const EMPTY = { full_name: "", full_name_ar: "", phone: "", email: "", license_number: "", license_class: "", license_expiry: "", hired_at: "", rating: 4.5, user_id: "", active: true, notes: "" };

export default function DriversPage() {
  const {  t , lang } = useI18n();
  const { schools, schoolId, setSchoolId, schoolName } = useSchools();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => { if (schoolId) api.get(`/drivers?school_id=${schoolId}`).then(r => setItems(r.data || [])); };
  useEffect(() => { load(); }, [schoolId]);
  useEffect(() => { api.get("/users?role=driver").then(r => setUsers(r.data || [])).catch(() => {}); }, []);

  const openEdit = (d) => { setEditing(d); setForm({ ...EMPTY, ...d }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, school_id: form.school_id || schoolId, rating: Number(form.rating) || 0, user_id: form.user_id || null };
      if (editing) await api.put(`/drivers/${editing.id}`, payload);
      else await api.post("/drivers", payload);
      toast.success(t("success")); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (d) => {
    try { await api.delete(`/drivers/${d.id}`); toast.success(t("success")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-3"><UserCircle2 className="w-7 h-7 text-primary" /> {t("drivers")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("drivers_added_via_hr")}</p>
        </div>
        <SchoolSelect schools={schools} value={schoolId} onChange={setSchoolId} testId="drivers-school-select" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? t("edit") : t("new_driver")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("nameAr")}</Label><Input value={form.full_name_ar} onChange={e => setForm({ ...form, full_name_ar: e.target.value })} data-testid="driver-name-ar" /></div>
                <div><Label>{t("nameEn")}</Label><Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} /></div>
                <div><Label>{t("phone") || "Phone"}</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>{t("email")}</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>{t("license_number")}</Label><Input value={form.license_number} onChange={e => setForm({ ...form, license_number: e.target.value })} /></div>
                <div><Label>{t("license_class")}</Label><Input value={form.license_class} onChange={e => setForm({ ...form, license_class: e.target.value })} placeholder="C2" /></div>
                <div><Label>{t("license_expiry")}</Label><Input type="date" value={form.license_expiry} onChange={e => setForm({ ...form, license_expiry: e.target.value })} /></div>
                <div><Label>{t("rating")}</Label><Input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={e => setForm({ ...form, rating: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label>Link to login user (role=driver, optional)</Label>
                  <Select value={form.user_id || "none"} onValueChange={v => setForm({ ...form, user_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {users.map(u => <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={save} className="w-full" data-testid="driver-save">{t("save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(d => {
          const expiring = d.license_expiry && new Date(d.license_expiry) < new Date(Date.now() + 90 * 86400000);
          return (
            <Card key={d.id} className="p-5 border shadow-none card-hover smooth" data-testid={`driver-${d.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent grid place-items-center"><UserCircle2 className="w-6 h-6" /></div>
                  <div>
                    <div className="font-semibold">{localFullName(d, lang)}</div>
                    <div className="text-xs text-muted-foreground">{d.phone}</div>
                  </div>
                </div>
                <RowActions item={d} onEdit={openEdit} onDelete={remove} testIdPrefix="driver" />
              </div>
              <div className="mt-3 space-y-1 text-xs">
                <div className="text-muted-foreground flex items-center gap-2" data-testid={`driver-school-${d.id}`}><Building2 className="w-3.5 h-3.5" /> {schoolName(d.school_id) || "—"}</div>
                <div className="text-muted-foreground flex items-center gap-2"><IdCard className="w-3.5 h-3.5" /> {d.license_number} <span className={expiring ? "text-destructive font-medium" : ""}>· {d.license_expiry}</span></div>
                <div className="flex items-center gap-1 text-amber-500"><Star className="w-3.5 h-3.5 fill-current" /> <span className="text-foreground font-medium">{d.rating || "—"}</span></div>
              </div>
            </Card>
          );
        })}
        {items.length === 0 && <Card className="col-span-full p-12 text-center text-muted-foreground border-dashed">{t("no_data")}</Card>}
      </div>
    </div>
  );
}
